import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Tag, message, Space, Card, Statistic, Row, Col, Select, Dropdown } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faTags } from '@fortawesome/free-solid-svg-icons';
import { callAnalyticsAPI } from '../../../services/callAnalytics';

const { TextArea } = Input;

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [stats, setStats] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadCategories();
    loadStats();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await callAnalyticsAPI.getCategories();
      setCategories(data);
    } catch (error) {
      message.error('Failed to load categories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await callAnalyticsAPI.getCategoryStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      color: 'blue'
    });
    setModalVisible(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      description: category.description,
      keywords: category.keywords?.join(', '),
      color: category.color || 'blue',
      isActive: category.isActive !== false
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await callAnalyticsAPI.deleteCategory(id);
      message.success('Category deleted successfully');
      loadCategories();
      loadStats();
    } catch (error) {
      message.error('Failed to delete category');
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const categoryData = {
        name: values.name,
        description: values.description,
        keywords: values.keywords ? values.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
        color: values.color,
        isActive: values.isActive,
        createdBy: editingCategory ? editingCategory.createdBy : 'user'
      };

      if (editingCategory) {
        await callAnalyticsAPI.updateCategory(editingCategory._id, categoryData);
        message.success('Category updated successfully');
      } else {
        await callAnalyticsAPI.createCategory(categoryData);
        message.success('Category created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      loadCategories();
      loadStats();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error(error.response?.data?.message || 'Failed to save category');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Category Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <Tag color={record.color || 'blue'}>{name}</Tag>
          {record.isSystemDefault && <Tag color="gold">System Default</Tag>}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Keywords',
      dataIndex: 'keywords',
      key: 'keywords',
      render: (keywords) => (
        <Space wrap>
          {keywords?.slice(0, 3).map((keyword, idx) => (
            <Tag key={idx} style={{ fontSize: '11px' }}>{keyword}</Tag>
          ))}
          {keywords?.length > 3 && <Tag>+{keywords.length - 3} more</Tag>}
        </Space>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (createdBy) => {
        const colorMap = {
          system: 'blue',
          ai: 'purple',
          user: 'green'
        };
        return <Tag color={colorMap[createdBy] || 'default'}>{createdBy}</Tag>;
      },
    },
    {
      title: 'Usage Count',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 120,
      sorter: (a, b) => a.usageCount - b.usageCount,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'edit',
            label: 'Edit',
            icon: <FontAwesomeIcon icon={faEdit} />,
            onClick: () => handleEdit(record),
            disabled: record.isSystemDefault && record.createdBy === 'system'
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <FontAwesomeIcon icon={faTrash} />,
            danger: true,
            disabled: record.isSystemDefault,
            onClick: () => {
              Modal.confirm({
                title: 'Are you sure you want to delete this category?',
                content: 'This will soft-delete the category (mark as inactive).',
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => handleDelete(record._id)
              });
            }
          }
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Categories"
                value={stats.total}
                prefix={<FontAwesomeIcon icon={faTags} />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="System Defaults"
                value={stats.systemDefault}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="AI Suggested"
                value={stats.aiSuggested}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="User Created"
                value={stats.userCreated}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Action Bar */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Call Categories</h2>
        <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleAdd}>
          Add Category
        </Button>
      </div>

      {/* Categories Table */}
      <Table
        columns={columns}
        dataSource={categories}
        loading={loading}
        rowKey="_id"
        pagination={{
          pageSize: 10,
          showTotal: (total) => `Total ${total} categories`,
        }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ isActive: true, color: 'blue' }}
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { max: 100, message: 'Name cannot exceed 100 characters' }
            ]}
          >
            <Input 
              placeholder="e.g., Recruiting, Scheduling, Assessment"
              disabled={editingCategory?.isSystemDefault}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter description' },
              { max: 500, message: 'Description cannot exceed 500 characters' }
            ]}
          >
            <TextArea 
              rows={3} 
              placeholder="Describe what types of calls belong to this category"
            />
          </Form.Item>

          <Form.Item
            name="keywords"
            label="Keywords (comma-separated)"
            help="AI will use these keywords to help categorize calls"
          >
            <Input 
              placeholder="e.g., hire, caregiver, job, position, employment"
            />
          </Form.Item>

          <Form.Item
            name="color"
            label="Display Color"
          >
            <Select>
              <Select.Option value="blue">Blue</Select.Option>
              <Select.Option value="green">Green</Select.Option>
              <Select.Option value="orange">Orange</Select.Option>
              <Select.Option value="red">Red</Select.Option>
              <Select.Option value="purple">Purple</Select.Option>
              <Select.Option value="cyan">Cyan</Select.Option>
              <Select.Option value="gold">Gold</Select.Option>
              <Select.Option value="magenta">Magenta</Select.Option>
              <Select.Option value="lime">Lime</Select.Option>
              <Select.Option value="geekblue">Geek Blue</Select.Option>
              <Select.Option value="volcano">Volcano</Select.Option>
              <Select.Option value="default">Default (Gray)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Select>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
